type PuppyKernel = ReturnType<typeof import('../../worker/engine/loader').getPuppyKernel>;

type PuppyBRepToolWithSeamProbe = {
  /** Puppy CAD 内核 用于判断边是否是某个面的闭合接缝边。 */
  IsClosed_2?: (edgeShape: unknown, faceShape: unknown) => boolean;
};

/**
 * 判断指定 Puppy CAD 内核 边是否是指定面的闭合接缝边。
 *
 * 设计说明：
 * 1. 闭合曲面上的 seam edge 是 Puppy CAD 内核 为表达参数域闭合而保留的内部拓扑边；
 * 2. 这类边不能暴露为 PuppyCAD 的普通特征边，否则圆柱等完整曲面会显示一条用户不应看到的接缝线；
 * 3. 该函数只负责读取 Puppy CAD 内核 的拓扑语义，不处理快照过滤，保持职责单一。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 待判断的 Puppy CAD 内核 边。
 * @param faceShape 候选归属 Puppy CAD 内核 面。
 * @returns `true` 表示该边是该面上的闭合接缝边。
 */
export function isClosedSeamEdgeOnFaceFromPuppyKernel(
  puppy: PuppyKernel,
  edgeShape: unknown,
  faceShape: unknown,
): boolean {
  const bRepTool = (puppy as Record<string, unknown>).BRep_Tool as PuppyBRepToolWithSeamProbe | undefined;
  const isClosedOnFace = bRepTool?.IsClosed_2;
  if (typeof isClosedOnFace !== 'function') {
    return false;
  }

  try {
    return Boolean(isClosedOnFace.call(bRepTool, edgeShape, faceShape));
  } catch {
    /*
     * seam 探测是拓扑快照的增强信息，Puppy CAD 内核 版本差异或 mock 不完整时不应中断主流程；
     * 返回 false 会保留原有边快照行为，避免因为可选能力缺失导致模型完全不可用。
     */
    return false;
  }
}

/**
 * 判断指定 Puppy CAD 内核 边是否命中了任意相邻面上的闭合接缝语义。
 *
 * @param puppy Puppy CAD 内核 实例。
 * @param edgeShape 待判断的 Puppy CAD 内核 边。
 * @param adjacentFaceShapes 相邻 Puppy CAD 内核 面集合。
 * @returns `true` 表示该边至少是一个相邻面的闭合接缝边。
 */
export function isClosedSeamEdgeAcrossFacesFromPuppyKernel(
  puppy: PuppyKernel,
  edgeShape: unknown,
  adjacentFaceShapes: unknown[],
): boolean {
  return adjacentFaceShapes.some((faceShape) => (
    isClosedSeamEdgeOnFaceFromPuppyKernel(puppy, edgeShape, faceShape)
  ));
}
